import type { ProdutoCatalogo } from "@/api"
import type { SxProps, Theme } from "@mui/material"
import { memo, useState } from "react"
import { Box, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material"
import { alpha, darken, useTheme } from "@mui/material/styles"
import { capitalize, formatarBRL } from "@/lib/formato"
import { urlImagemProduto } from "@/lib/imagem"
import { produtoIndisponivel, temPassos } from "./passos"
import { fundoNeutroKiosk } from "./cores-grupo"
import CarrosselImagens from "./carrossel-imagens"
import AddIcon from '@mui/icons-material/Add'
import TuneIcon from '@mui/icons-material/Tune'
import CloseIcon from '@mui/icons-material/Close'
import RemoveIcon from '@mui/icons-material/Remove'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import { precoPorQuantidade } from "@/lib/preco-por-quantidade"
import { PrecoProduto } from "@/components/preco-produto"
import { SeloNovo } from "@/components/selo-novo"

interface CardProdutoProps {
    produto: ProdutoCatalogo
    cor: string | null
    quantidade?: number
    onAdicionar?: (produto: ProdutoCatalogo) => void
    onRemover?: (produto: ProdutoCatalogo) => void
    onSelecionar?: (produto: ProdutoCatalogo) => void
    precoExibido?: number
    /** Cartão estreito (tablet): com item no carrinho, o seletor de quantidade e o preço encolhem para caber na mesma linha. */
    seletorCompacto?: boolean
    /** O ⓘ abre os detalhes do produto; sem ele, o ⓘ mostra a ficha técnica. */
    onAbrirDetalhes?: (produto: ProdutoCatalogo) => void
    /** Tocar na foto também abre os detalhes (tablet). */
    detalhesNaFoto?: boolean
}

// O h4 do kiosk é 2.5vmax; com o seletor aberto no cartão estreito o preço cede um pouco, senão o seletor desce para a linha de baixo.
const FONTE_VALOR_APERTADO = 'clamp(18px, 2.2vmax, 24px)'
// Abaixo disto nem o compacto cabe ao lado do preço (tablet em pé, dois cartões por linha): o botão vai para a linha de baixo.
const CARTAO_ESTREITO = '@container (max-width: 215px)'

function DialogInfoTecnica({ produto, infoAberto, handleClose, fichaTecnica, advertencias }: any) {
    return (
        <Dialog open={infoAberto} onClose={handleClose} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {capitalize(produto.descricao)}

                <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {infoAberto && (
                    <Box sx={{ mb: 3 }}>
                        <CarrosselImagens imagens={produto.imagens} alt={produto.descricao} />
                    </Box>
                )}

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Descrição
                    </Typography>

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-line', fontStyle: fichaTecnica ? 'normal' : 'italic' }}
                    >
                        {fichaTecnica ?? 'Sem descrição'}
                    </Typography>
                </Box>

                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', mb: 1, color: 'text.primary' }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Advertências
                        </Typography>
                    </Stack>

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-line', fontStyle: advertencias ? 'normal' : 'italic' }}
                    >
                        {advertencias ?? 'Sem advertências'}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function CardProduto({ produto, quantidade = 0, onAdicionar, onRemover, onSelecionar, precoExibido, seletorCompacto = false, onAbrirDetalhes, detalhesNaFoto = false }: CardProdutoProps): JSX.Element {
    const precoDaQuantidade = precoPorQuantidade(
        produto.faixasPreco,
        Math.max(quantidade, 1),
        produto.valor,
    )
    const theme = useTheme()
    const selecionavel = !!onSelecionar
    const personalizavel = temPassos(produto)
    const indisponivel = produtoIndisponivel(produto)
    const temQuantidade = quantidade > 0
    const mostraSeletor = !selecionavel && !personalizavel && temQuantidade
    const apertado = seletorCompacto && mostraSeletor
    const estiloValor: SxProps<Theme> = {
        fontWeight: 'bold',
        color: 'success.main',
        lineHeight: 1.2,
        ...(apertado ? { fontSize: FONTE_VALOR_APERTADO } : {}),
    }
    const aDireitaSeEmpilhado: SxProps<Theme> = seletorCompacto ? { [CARTAO_ESTREITO]: { alignSelf: 'flex-end' } } : {}
    // Estreito na largura, alto no toque: o botão ocupa quase a altura toda da pílula.
    const estiloBotaoSeletor: SxProps<Theme> = apertado ? { p: 0, width: 30, height: 42 } : { p: 0.5 }
    const imagem = produto.imagens.find((i) => i.preferencial) ?? produto.imagens[0]
    const imageUrl = urlImagemProduto(imagem, { largura: 320, altura: 320, modo: 'cover' })
    const fichaTecnica = (produto.descricaoComposicao ?? produto.informacoesTecnicas)?.trim() || null
    const advertencias = produto.advertencias?.trim() || null
    const [infoAberto, setInfoAberto] = useState(false)

    const handleCloseDialogInfoTecnica = () => {
        setInfoAberto(false)
    }

    const handleAdd = (): void => onAdicionar?.(produto)
    const handleRemove = (): void => onRemover?.(produto)

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                display: 'flex',
                overflow: 'hidden',
                containerType: seletorCompacto ? 'inline-size' : undefined,
                border: temQuantidade ? '1.5px solid': undefined,
                flexDirection: 'column',
                transition: 'border-color 0.2s',
                color: 'text.primary',
                backgroundColor: fundoNeutroKiosk(theme),
                borderColor: temQuantidade ? 'primary.main' : undefined,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '1/1',
                    backgroundColor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    flexShrink: 0,
                }}
            >
                {imageUrl ? (
                    <Box component="img" src={imageUrl} alt={produto.descricao} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <Stack spacing={1} sx={{ alignItems: 'center' }}>
                        <ImageNotSupportedOutlinedIcon sx={{ fontSize: 64, opacity: 0.24 }} />
                        <Typography variant="body1" color="text.secondary">
                            Sem imagem
                        </Typography>
                    </Stack>
                )}

                {/* Camada de toque sobre a foto, antes do ⓘ: ele segue por cima e não vira botão dentro de botão. */}
                {detalhesNaFoto && onAbrirDetalhes && (
                    <Box
                        component="button"
                        type="button"
                        aria-label={`Ver detalhes de ${produto.descricao}`}
                        onClick={() => onAbrirDetalhes(produto)}
                        sx={{ position: 'absolute', inset: 0, p: 0, border: 0, background: 'transparent', cursor: 'pointer' }}
                    />
                )}

                <Stack
                    direction="row"
                    sx={{
                        top: 8,
                        left: 8,
                        right: 8,
                        position: 'absolute',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pointerEvents: 'none',
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation()
                            if (onAbrirDetalhes) onAbrirDetalhes(produto)
                            else setInfoAberto(true)
                        }}
                        aria-label="Informações do produto"
                        sx={{
                            pointerEvents: 'auto',
                            color: 'text.primary',
                            backgroundColor: alpha(theme.palette.background.paper, 0.85),
                            '&:hover': { backgroundColor: 'background.paper' },
                        }}
                    >
                        <InfoOutlinedIcon />
                    </IconButton>

                    {produto.emPromocao && (
                        <Chip icon={<LocalOfferIcon />} label={<Typography>Promo</Typography>} color="error" size="small" sx={{ pointerEvents: 'auto', py: 2, px: 1 }} />
                    )}
                </Stack>

                {!selecionavel && temQuantidade && (
                    <Box
                        sx={{
                            right: 8,
                            bottom: 8,
                            width: 28,
                            height: 28,
                            display: 'flex',
                            borderRadius: '50%',
                            alignItems: 'center',
                            position: 'absolute',
                            justifyContent: 'center',
                            backgroundColor: 'primary.main',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', lineHeight: 1 }}>
                            {quantidade}
                        </Typography>
                    </Box>
                )}
            </Box>

            <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, lineHeight: 1.3 }}>
                    {capitalize(produto.descricao)}
                    {produto.novo && <SeloNovo />}
                </Typography>

                {fichaTecnica && (
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            color: 'text.secondary',
                        }}
                    >
                        {fichaTecnica}
                    </Typography>
                )}

                {/* Produto com passo obrigatório sem nenhuma opção (a loja pausou todas) não pode ser
                    pedido. O "+" continua abrindo o wizard de propósito: é lá que o cliente lê o
                    motivo — desabilitar aqui deixaria um toque sem resposta. */}
                {!selecionavel && indisponivel ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5, color: 'warning.main' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Indisponível agora
                        </Typography>
                    </Stack>
                ) : !selecionavel && personalizavel ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5, color: 'text.secondary' }}>
                        <TuneIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Personalizável
                        </Typography>
                    </Stack>
                ) : null}

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        mt: 'auto',
                        pt: 1,
                        gap: 1,
                        flexWrap: 'wrap',
                        ...(seletorCompacto ? { [CARTAO_ESTREITO]: { flexDirection: 'column', alignItems: 'stretch', flexWrap: 'nowrap' } } : {}),
                    }}
                >
                    {/* Piso dos passos e "a partir de" seguem a regra do delivery; a faixa por quantidade troca só o preço do pai. */}
                    {precoExibido !== undefined ? (
                        <Typography variant="h4" sx={estiloValor}>
                            {formatarBRL(precoExibido)}
                        </Typography>
                    ) : (
                        <PrecoProduto
                            produto={{ ...produto, valor: precoDaQuantidade }}
                            sx={{ minWidth: 0 }}
                            valor={{ variant: 'h4', sx: estiloValor }}
                        />
                    )}

                    {!mostraSeletor ? (
                        <IconButton
                            onClick={selecionavel ? () => onSelecionar!(produto) : handleAdd}
                            aria-label={`${selecionavel ? 'Selecionar' : 'Adicionar'} ${produto.descricao}`}
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                color: 'primary.contrastText',
                                backgroundColor: 'primary.main',
                                '&:hover': { backgroundColor: darken(theme.palette.primary.main, 0.1) },
                                ...aDireitaSeEmpilhado,
                            }}
                        >
                            <AddIcon fontSize="medium" />
                        </IconButton>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: 50, p: apertado ? 0.25 : 0.5, backgroundColor: 'action.hover', height: 50, flexShrink: 0, ...aDireitaSeEmpilhado }}>
                            <IconButton size="large" onClick={handleRemove} aria-label={`Remover ${produto.descricao}`} sx={estiloBotaoSeletor}>
                                <RemoveIcon />
                            </IconButton>

                            <Typography variant="h5" sx={{ fontWeight: 'bold', minWidth: apertado ? 22 : 32, textAlign: 'center' }}>
                                {quantidade}
                            </Typography>

                            <IconButton size="large" onClick={handleAdd} aria-label={`Adicionar ${produto.descricao}`} sx={estiloBotaoSeletor}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                    )}
                </Stack>

            </CardContent>

            <DialogInfoTecnica
                produto={produto}
                infoAberto={infoAberto}
                advertencias={advertencias}
                fichaTecnica={fichaTecnica}
                handleClose={handleCloseDialogInfoTecnica}
            />
        </Card>
    )
}

export default memo(CardProduto)
